import { TCustomerDataArrays } from '@/core/data_handlers/customer_data_new/types';
import { SHA256 } from '@/core/event_logic/utils/hash_utils';

import { TGtagUserData, TGtagUserDataAddress } from './types';

// Function to normalize and hash data
async function hashAndNormalize(data: string): Promise<string | null> {
  if (!data) return null;
  // Normalize the data
  data = data.trim().toLowerCase();
  // Hash the data using SHA-256
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  data = await SHA256(data);
  return data;
}

// Function to process the customer data
export async function processCustomerData(
  customerData: TCustomerDataArrays,
): Promise<Record<string, string[]>> {
  const googleUserDataKv: Record<string, string[]> = {};

  // Helper function to process each field
  async function processField(key: string, data: string[]) {
    const hashedData = await Promise.all(data.map(hashAndNormalize));
    if (hashedData.length > 0) {
      googleUserDataKv[key] = hashedData.filter(
        (item): item is string => item !== null,
      );
    }
  }

  // Process each field

  await Promise.all([
    processField('sha256_email_address', customerData.em),
    processField('sha256_phone_number', customerData.ph),
    processField('sha256_first_name', customerData.fn),
    processField('sha256_last_name', customerData.ln),
    processField('sha256_street', customerData.street),
    processField('sha256_city', customerData.ct),
    processField('sha256_region', customerData.rg),
    processField('sha256_postal_code', customerData.zip.map(String)),
    processField('sha256_country', customerData.cc),
  ]);

  return googleUserDataKv;
}

/**
 * Helper function to process and set user data
 * @param data - The data to process
 * @param obj - The object to set the data on
 * @param key - The key to set the data on
 * @param hash - Whether to hash the data
 * @param isSingle - Whether the data required is a single value
 * @returns
 */
async function processAndSetUserData(
  data: string[],
  obj: TGtagUserData,
  key: keyof TGtagUserData,
  hash = false,
) {
  if (!obj) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    obj = {} as TGtagUserData;
  }
  // If there is no data, we don't need to process it
  if (!data || data.length === 0) return;

  if (hash) {
    (obj as any)[key] = await Promise.all(data.map(hashAndNormalize));
  } else {
    (obj as any)[key] = data;
  }
}

/**
 * Processes address data into the Google Ads format
 */
async function processUserAddress(
  customerData: TCustomerDataArrays,
): Promise<TGtagUserDataAddress[]> {
  const addresses: TGtagUserDataAddress[] = [];

  // Find the maximum length of all address-related arrays
  const maxAddresses = Math.max(
    customerData.ct?.length ?? 0,
    customerData.rg?.length ?? 0,
    customerData.zip?.length ?? 0,
    customerData.street?.length ?? 0,
    customerData.fn?.length ?? 0,
    customerData.ln?.length ?? 0,
  );

  // eslint-disable-next-line unicorn/consistent-function-scoping
  async function setHashedValue(
    data: string | undefined,
    obj: any,
    key: string,
  ) {
    if (!data) return;
    const hashedValue = await hashAndNormalize(data);
    if (hashedValue) {
      obj[key] = hashedValue;
    }
  }

  const promises: Promise<void>[] = [];
  // Process each possible address
  for (let i = 0; i < maxAddresses; i++) {
    const address: TGtagUserDataAddress = {};

    promises.push(
      setHashedValue(customerData.fn?.[i], address, 'sha256_first_name'),
      setHashedValue(customerData.ln?.[i], address, 'sha256_last_name'),
    );

    // Process non-hashed fields
    if (customerData.street?.[i]) address.street = customerData.street[i];
    if (customerData.ct?.[i]) address.city = customerData.ct[i];
    if (customerData.rg?.[i]) address.region = customerData.rg[i];
    if (customerData.zip?.[i])
      address.postal_code = String(customerData.zip[i]);
    if (customerData.cc?.[i]) address.country = customerData.cc[i];

    addresses.push(address);
  }

  await Promise.all(promises);

  return addresses;
}

/**
 * Processes the Mable Customer Data Arrays into expected Google Ads User Data
 */
export async function processGoogleAdsUserData(
  customerData: TCustomerDataArrays,
): Promise<TGtagUserData> {
  const userData: TGtagUserData = {};

  // Process email and phone
  await Promise.all([
    processAndSetUserData(
      customerData.em,
      userData,
      'sha256_email_address',
      true,
    ),
    // processAndSetUserData(customerData.em, userData, 'email', false),
    processAndSetUserData(
      customerData.ph,
      userData,
      'sha256_phone_number',
      true,
    ),
    // processAndSetUserData(customerData.ph, userData, 'phone_number', false),
  ]);

  // Process addresses
  const addresses = await processUserAddress(customerData);
  if (addresses.length > 0) {
    userData.address = addresses;
  }

  return userData;
}
